import axios from "axios";
import { BOOT_AT, markDirty, readCache, remoteChange, writeCache } from "@/lib/cache";
import { enqueue, flush, localPhotoUrl } from "@/lib/outbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 120000,
  // Required for Emergent Google Auth so the session_token httpOnly cookie
  // is sent on every API request.
  withCredentials: true,
});

// GET with `swr`: answers from the saved copy. A copy fetched since the app opened (and not made old by a save) is used
// with no server call at all; an older one shows at once and is re-checked once in the background, calling swr(data) if it changed.
// Only the latest request per `swrGroup` (default: the path) gets the update, so a late answer for an old filter can't win.
// Identical GETs in flight at the same time share one server call.
const network = axios.getAdapter(api.defaults.adapter);
const parse = (d) => { if (typeof d !== "string") return d; try { return JSON.parse(d); } catch { return d; } };
const latest = new Map();
const inflight = new Map();
let seq = 0;
const shared = (config) => {
  const k = `${config.responseType || ""}|${api.getUri(config)}`;
  if (!inflight.has(k)) inflight.set(k, network(config).finally(() => inflight.delete(k)));
  return inflight.get(k);
};
// Background calls that don't change what lists show; other saves mark the saved copies old.
const KEEPS_CACHE = /^\/?(research-mode|push\/|auth\/|analytics|events|edgar\/|local\/|team\/packed)/;
const soldSomething = (res) => { const d = parse(res.data); return !!(d?.newly_sold || d?.newly_sold_count); };
// Live / polled answers that must always come from the server (progress, payments, status checks, sign-in).
const NO_CACHE = /^\/?(auth\/|photo-batches\/(?!leaderboard$|listers$|free-finds$|lister-queue$)[^/?]+(\?|$)|photo-batches\/(done-alerts|leaderboard|lister-queue)|media-batches\/[^/?]+(\?|$)|ebay\/my-listings\/refresh|inventory\/sku-notes\/export|ebay\/status|ebay\/connect|ebay\/policies|whatnot\/status|payments\/|edgar\/team-usage|usage\/|questions|team\/nudges|inbox|research-export|ebay\/pick-list|extension\/|paste\/)/;
// Settings that rarely change: reused for a day across app opens (Refresh or any save re-checks them).
const DAY_CACHE = /^\/?(research\/settings|push\/key|stats\/listing-goal|team(\/members|\/me)?$|categories|ebay\/categories|preferences|settings|billing\/plan|plans)/;
// Lists that only change when drafts / inventory change: reused across app opens until the server reports something newer.
const DATA_CACHE = /^\/?(inventory\/(bins|locations|sku-notes)$|drafts\/(skus|listed-summary)$)/;
const stillCurrent = (entry, url) => DATA_CACHE.test(url) && !!remoteChange() && Date.parse(remoteChange()) < entry.at;
const QUEUEABLE = (c) => !c._outbox && ((c.method === "post" && /^\/?(drafts|photos\/upload)$/.test(c.url || "")) || (c.method === "patch" && /^\/?drafts\/[^/?]+$/.test(c.url || "")));
const noSignal = (e) => !e?.response;
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) => (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)));
const fakeRes = (config, data) => ({ data, status: 202, statusText: "Queued", headers: {}, config, request: null, queued: true });

// Draft saves and photo uploads made with no signal wait on the phone and go out when it's back.
async function queueOrSend(config) {
  const photo = config.data instanceof FormData;
  if (photo && !config.data.get("id")) config.data.append("id", uuid());
  const park = async () => {
    if (photo) {
      const file = config.data.get("file");
      const id = config.data.get("id");
      await enqueue({ kind: "photo", url: config.url, file, filename: file?.name || "photo.jpg", photoId: id });
      return fakeRes(config, { id, filename: file?.name || "photo.jpg", queued: true });
    }
    await enqueue({ kind: "req", method: config.method, url: config.url, data: config.data });
    return fakeRes(config, { ...(parse(config.data) || {}), queued: true });
  };
  if (!navigator.onLine) return park();
  try { return await network(config); } catch (e) { if (noSignal(e)) return park(); throw e; }
}

export async function sendQueued(it) {
  try {
    if (it.kind === "photo") {
      const form = new FormData();
      form.append("file", it.file, it.filename);
      form.append("id", it.photoId);
      await api.post(it.url, form, { _outbox: true });
    } else {
      await api.request({ method: it.method, url: it.url, data: it.data, headers: { "Content-Type": "application/json" }, _outbox: true });
    }
  } catch (e) {
    if (noSignal(e)) throw Object.assign(new Error("offline"), { offline: true });
    const d = e?.response?.data?.detail;
    throw new Error(typeof d === "string" ? d : "server said no");
  }
}
export const flushOutbox = () => flush(sendQueued);
// Once queued edits reach the server, reload open screens so nothing shows (and re-saves) the older copy.
window.addEventListener("snaplist:outbox-sent", () => refreshApp());

api.defaults.adapter = async (config) => {
  if (/^\/?ebay\/sync-listings/.test(config.url || "")) return network(config).then((res) => { if (soldSomething(res)) markDirty(); return res; });
  if (config.method !== "get") return (QUEUEABLE(config) ? queueOrSend(config) : network(config)).then((res) => { if (!KEEPS_CACHE.test(config.url || "")) markDirty(); return res; });
  try {
    return await cachedGet(config);
  } catch (e) {
    if (!noSignal(e)) throw e;
    const old = await readCache(api.getUri(config), true); // no signal: show the last saved copy, even an old one
    if (old) return { data: old.data, status: 200, statusText: "OK", headers: {}, config, request: null, cached: true, offline: true };
    throw e;
  }
};

async function cachedGet(config) {
  if (!config.swr && (config.fresh || config.responseType === "blob" || NO_CACHE.test(config.url || ""))) return shared(config);
  const key = api.getUri(config);
  const group = config.swrGroup || config.url;
  const id = ++seq;
  latest.set(group, id);
  const entry = await readCache(key);
  const hit = entry?.data;
  const cachedRes = () => ({ data: hit, status: 200, statusText: "OK", headers: {}, config, request: null, cached: true });
  if (entry && (entry.at >= BOOT_AT || (DAY_CACHE.test(config.url || "") && Date.now() - entry.at < 864e5) || stillCurrent(entry, config.url || ""))) return cachedRes();
  const fresh = shared(config).then((res) => {
    const data = parse(res.data);
    const s = JSON.stringify(data);
    writeCache(key, data);
    return { res, data, changed: !entry || s !== JSON.stringify(hit) };
  });
  if (!entry) return fresh.then((f) => f.res);
  fresh.then((f) => { if (f.changed && latest.get(group) === id && typeof config.swr === "function") config.swr(f.data); }).catch(() => {});
  return cachedRes();
}

// Refresh button: everything saved so far counts as old, and the screen reloads from the server.
export const refreshApp = () => { markDirty(); window.dispatchEvent(new Event("snaplist:refresh")); };

// 402 = free-tier cap / Pro-only feature → pop the upgrade dialog.
api.interceptors.response.use(undefined, (error) => {
  const detail = error?.response?.data?.detail;
  // Remember the last failed action so an assistant can send it to the owner in a nudge.
  if (error?.config?.method !== "get" && (typeof detail === "string" || error?.response?.status >= 500)) {
    try { sessionStorage.setItem("snaplist.lastError", JSON.stringify({ text: typeof detail === "string" ? detail : "Server error", at: Date.now(), path: window.location.pathname + window.location.search })); } catch { /* ignore */ }
  }
  if (error?.response?.status === 402 && detail?.code === "free_tier_cap_reached") {
    window.dispatchEvent(new CustomEvent("snaplist:upgrade", { detail }));
  }
  if (error?.response?.status === 402 && detail?.code === "plan_required" && window.location.pathname !== "/billing") {
    window.location.assign("/billing");
  }
  return Promise.reject(error);
});

// Preset sizes map to backend presets (thumb=160, card=320, hero=800).
// Omit `size` to get the original full-resolution image.
export const photoUrl = (id, size, v) => {
  const local = localPhotoUrl(id);
  if (local) return local;
  const qs = [];
  if (size) qs.push(`size=${encodeURIComponent(size)}`);
  if (v) qs.push(`v=${encodeURIComponent(v)}`);
  return qs.length ? `${API}/photos/${id}?${qs.join("&")}` : `${API}/photos/${id}`;
};
